/**
 * Character limits for string fields, shared between the create and edit
 * flows so the two validation paths cannot drift apart (a string created
 * within these limits must remain valid when reopened for editing).
 */
export const SOURCE_TEXT_CHAR_LIMIT = 10000;
export const CONTEXT_NOTE_CHAR_LIMIT = 5000;

/**
 * Upper bound on pages fetched when walking a version snapshot's paginated
 * strings. Shared by every snapshot reader so a backend that never returns a
 * null nextToken cannot hang the UI in an unbounded loop.
 */
export const MAX_SNAPSHOT_PAGES = 100;

/**
 * Page size requested when walking a version snapshot's strings. Pinned (rather
 * than left to the backend default) so MAX_SNAPSHOT_PAGES bounds the same
 * number of strings for every reader.
 */
export const SNAPSHOT_PAGE_SIZE = 100;

/**
 * Maximum strings a single group may hold. Shared by the create form's
 * validation and the strings table header so the advertised limit and the
 * enforced limit cannot drift apart.
 */
export const MAX_STRINGS_PER_GROUP = 25;

/**
 * Maximum length of a string ID. Matches the backend Smithy constraint:
 * `@length(min: 1, max: 200)` on `StringIdentifier`.
 */
export const STRING_ID_MAX_LENGTH = 200;

/**
 * Regex for valid string IDs — must contain only letters, digits, hyphens, or
 * underscores and include at least one alphanumeric character. Matches the
 * backend Smithy constraint: `(?=.*[a-zA-Z0-9])[a-zA-Z0-9_-]+`
 */
export const STRING_ID_PATTERN = /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9_-]+$/;

/**
 * User-facing error message when a string ID fails the pattern check.
 */
export const STRING_ID_FORMAT_ERROR =
  'String ID must contain only letters, digits, hyphens, or underscores and include at least one alphanumeric character.';

/**
 * User-facing error message when a string ID exceeds the maximum length.
 */
export const STRING_ID_MAX_LENGTH_ERROR =
  'String ID must be at most 200 characters.';

/**
 * Flashbar copy for the transient "string group not ready" state (backend 409
 * mapped to StringGroupNotReadyException): a fresh submission published
 * successfully but TIS has not finished ingesting the new version yet, so it is
 * not queryable. Shared with the detail page's tests so the asserted copy and
 * the rendered copy cannot drift apart.
 */
export const STRING_GROUP_NOT_READY_HEADER = 'Changes still processing';
export const STRING_GROUP_NOT_READY_MESSAGE =
  'Your recent changes are still being processed. This usually takes a couple ' +
  "of minutes. Please refresh the page if you don't see them right away.";
